import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  UserPlusIcon,
  XMarkIcon,
  Cog6ToothIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { useAdmins } from "../hooks/useAdmins";
import { useAdminStats } from "../hooks/useAdminStats";
import { AdminTable } from "../components/AdminTable";
import { useAuth } from "../../../contexts/AuthContext";
import { searchAdminCandidateByEmail, type AdminUser } from "@/shared/db";
import { Button, Input, Select } from "@/shared/ui";
import { useConfirm } from "@/hooks/useConfirm";

export const AdminSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user, adminRole } = useAuth();
  const {
    admins,
    loading,
    error,
    createAdmin,
    updateAdminRole,
    deactivateAdmin,
  } = useAdmins();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<AdminUser | null>(null);
  const [formData, setFormData] = useState({
    userId: "",
    role: "admin" as "super_admin" | "admin" | "moderator",
  });
  const [emailSearch, setEmailSearch] = useState("");
  const [searchResults, setSearchResults] = useState<
    { id: string; email: string; full_name: string }[]
  >([]);
  const [searching, setSearching] = useState(false);

  const isSuperAdmin = adminRole === "super_admin";
  const { confirm, Modal: ConfirmModal } = useConfirm();
  const { stats: platformStats } = useAdminStats();

  const handleEmailSearch = useCallback(async () => {
    if (!emailSearch.trim()) return;
    setSearching(true);
    try {
      const results = await searchAdminCandidateByEmail(emailSearch.trim());
      setSearchResults(results);
    } finally {
      setSearching(false);
    }
  }, [emailSearch]);

  const selectCandidate = (c: { id: string; email: string }) => {
    setFormData((f) => ({ ...f, userId: c.id }));
    setEmailSearch(c.email);
    setSearchResults([]);
  };

  const handleCreate = async () => {
    if (!formData.userId.trim()) return;
    try {
      await createAdmin(formData.userId, formData.role);
      setShowCreateModal(false);
      setFormData({ userId: "", role: "admin" });
      setEmailSearch("");
      setSearchResults([]);
    } catch {
      // Error already handled in hook
    }
  };

  const handleEdit = (admin: AdminUser) => {
    setEditingAdmin(admin);
    setFormData({ userId: admin.id, role: admin.role });
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    if (!editingAdmin) return;
    try {
      await updateAdminRole(editingAdmin.id, formData.role);
      setShowEditModal(false);
      setEditingAdmin(null);
      setFormData({ userId: "", role: "admin" });
    } catch (err) {
      // Error already handled in hook
    }
  };

  const handleDeactivate = async (adminId: string) => {
    const confirmed = await confirm({
      title: t("admin.admins.confirmDeactivate"),
      message: t("admin.admins.confirmDeactivateMessage"),
      confirmText: t("common.deactivate"),
      variant: "danger",
    });
    if (!confirmed) return;
    try {
      await deactivateAdmin(adminId);
    } catch {
      // Error already handled in hook
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-display font-bold text-tiber-900">
          {t("admin.settings.title")}
        </h1>
        <p className="text-sm font-light text-tiber-600/60 mt-1">
          {t("admin.settings.subtitle")}
        </p>
      </div>

      {/* Admin Management Section */}
      {!isSuperAdmin ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg border border-romance-200 p-6 text-center py-16"
        >
          <p className="text-tiber-500">{t("admin.settings.noPermissions")}</p>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg border border-romance-200 p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-tiber-900">
                {t("admin.settings.admins.title")}
              </h2>
              <p className="text-sm font-light text-tiber-600/60 mt-1">
                {t("admin.settings.admins.subtitle")}
              </p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-tiber-500 text-white rounded-lg hover:bg-tiber-500/90 transition-colors"
            >
              <UserPlusIcon className="w-5 h-5" />
              <span>{t("admin.admins.actions.create")}</span>
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tiber-500"></div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-red-600">{error}</p>
            </div>
          ) : admins.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-juridico/60">
                {t("admin.settings.admins.noAdmins")}
              </p>
            </div>
          ) : (
            <AdminTable
              admins={admins}
              onEdit={handleEdit}
              onDeactivate={handleDeactivate}
              currentUserId={user?.id}
            />
          )}
        </motion.div>
      )}

      {/* System Settings Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-lg border border-romance-200 p-6"
      >
        <div className="flex items-center space-x-3 mb-6">
          <Cog6ToothIcon className="w-6 h-6 text-tiber-500" />
          <div>
            <h2 className="text-xl font-bold text-tiber-900">
              {t("admin.settings.system.title")}
            </h2>
            <p className="text-sm font-light text-tiber-600/60 mt-1">
              {t("admin.settings.system.subtitle")}
            </p>
          </div>
        </div>
        <h3 className="text-sm font-semibold text-juridico/60 mb-3">
          {t("admin.settings.system.platformStats")}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="p-4 bg-romance-50 rounded-lg">
            <p className="text-xs text-juridico/60 font-medium">{t("admin.settings.system.totalUsers")}</p>
            <p className="text-2xl font-bold text-juridico mt-1">{platformStats?.totalUsers ?? 0}</p>
          </div>
          <div className="p-4 bg-romance-50 rounded-lg">
            <p className="text-xs text-juridico/60 font-medium">{t("admin.settings.system.totalDocuments")}</p>
            <p className="text-2xl font-bold text-juridico mt-1">{platformStats?.totalDocuments ?? 0}</p>
          </div>
          <div className="p-4 bg-romance-50 rounded-lg">
            <p className="text-xs text-juridico/60 font-medium">{t("admin.settings.system.platformVersion")}</p>
            <p className="text-2xl font-bold text-juridico mt-1">1.0.0</p>
          </div>
        </div>
      </motion.div>

      {/* Create Admin Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowCreateModal(false)}
              className="fixed inset-0 bg-black/50 z-50"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 flex items-center justify-center z-50 p-4"
            >
              <div
                className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-tiber-900">
                    {t("admin.admins.create.title")}
                  </h3>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="text-sm font-light text-tiber-600/60 hover:text-tiber-900 transition-colors"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-tiber-900 mb-1">
                      {t("admin.admins.create.searchByEmail", "Buscar por email")}
                    </label>
                    <div className="flex gap-2">
                      <Input
                        type="email"
                        value={emailSearch}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setEmailSearch(e.target.value);
                          setSearchResults([]);
                        }}
                        placeholder="usuario@ejemplo.com"
                        onKeyDown={(e) =>
                          e.key === "Enter" && (e.preventDefault(), handleEmailSearch())
                        }
                      />
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={handleEmailSearch}
                        disabled={searching || !emailSearch.trim()}
                      >
                        <MagnifyingGlassIcon className="w-5 h-5" />
                      </Button>
                    </div>
                    {searchResults.length > 0 && (
                      <ul className="mt-2 border border-tiber-200 rounded-lg divide-y divide-tiber-100 max-h-40 overflow-auto">
                        {searchResults.map((c) => (
                          <li key={c.id}>
                            <button
                              type="button"
                              onClick={() => selectCandidate(c)}
                              className="w-full px-4 py-2 text-left hover:bg-tiber-50 text-sm"
                            >
                              <span className="font-medium">{c.email}</span>
                              {c.full_name && (
                                <span className="text-tiber-600 ml-2">
                                  — {c.full_name}
                                </span>
                              )}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div>
                    <Input
                      label={t("admin.admins.create.userId")}
                      type="text"
                      value={formData.userId}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFormData({ ...formData, userId: e.target.value })
                      }
                      placeholder={t("admin.admins.create.userIdPlaceholder")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-juridico mb-2">
                      {t("admin.admins.create.role")}
                    </label>
                    <Select
                      label={t("admin.admins.create.role")}
                      value={formData.role}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        setFormData({
                          ...formData,
                          role: e.target.value as
                            | "super_admin"
                            | "admin"
                            | "moderator",
                        })
                      }
                      options={[
                        {
                          value: "admin",
                          label: t("admin.admins.create.admin"),
                        },
                        {
                          value: "moderator",
                          label: t("admin.admins.create.moderator"),
                        },
                        {
                          value: "super_admin",
                          label: t("admin.admins.create.superAdmin"),
                        },
                      ]}
                    />
                  </div>
                  <div className="flex space-x-3 pt-4">
                    <Button onClick={handleCreate}>{t("common.create")}</Button>
                    <Button
                      onClick={() => setShowCreateModal(false)}
                      variant="secondary"
                    >
                      {t("common.cancel")}
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Edit Admin Modal */}
      <AnimatePresence>
        {showEditModal && editingAdmin && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowEditModal(false)}
              className="fixed inset-0 bg-black/50 z-50"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 flex items-center justify-center z-50 p-4"
            >
              <div
                className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-juridico">
                    {t("admin.admins.edit.title")}
                  </h3>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="text-juridico/60 hover:text-juridico transition-colors"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-juridico/60 mb-2">
                      {editingAdmin.email || editingAdmin.id}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-juridico mb-2">
                      {t("admin.admins.edit.role")}
                    </label>
                    <select
                      value={formData.role}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          role: e.target.value as
                            | "super_admin"
                            | "admin"
                            | "moderator",
                        })
                      }
                      className="w-full px-4 py-2 border border-romance-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-tiber-500"
                    >
                      <option value="admin">
                        {t("admin.admins.create.admin")}
                      </option>
                      <option value="moderator">
                        {t("admin.admins.create.moderator")}
                      </option>
                      <option value="super_admin">
                        {t("admin.admins.create.superAdmin")}
                      </option>
                    </select>
                  </div>
                  <div className="flex space-x-3 pt-4">
                    <button
                      onClick={handleUpdate}
                      className="flex-1 px-4 py-2 bg-tiber-500 text-white rounded-lg hover:bg-tiber-500/90 transition-colors"
                    >
                      {t("common.save")}
                    </button>
                    <button
                      onClick={() => setShowEditModal(false)}
                      className="flex-1 px-4 py-2 border border-romance-200 text-juridico rounded-lg hover:bg-romance-50 transition-colors"
                    >
                      {t("common.cancel")}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {ConfirmModal}
    </div>
  );
};
